import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  forms: {},
  flows: {},
};

const INVOICE_FORM_KEY = "invoice";
const INVOICE_PRESERVE_KEYS = [
  "branch_id",
  "branchId",
  "warehouse_id",
  "warehouseId",
  "cash_register_id",
  "cashRegisterId",
  "price_list_id",
  "priceListId",
  "currency",
];

const getFormKey = (payload) =>
  payload?.form ?? payload?.formName ?? payload?.name ?? payload?.key ?? null;

const toObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value;
  }
  return {};
};

const normalizeLine = (line) => {
  if (!line || typeof line !== "object" || Array.isArray(line)) {
    return line;
  }

  const next = { ...line };
  if (next.product && typeof next.product === "object" && next.product_id == null) {
    next.product_id = next.product.id ?? null;
  }
  if (next.variant && typeof next.variant === "object" && next.variant_id == null) {
    next.variant_id = next.variant.id ?? null;
  }
  delete next.product;
  delete next.variant;
  return next;
};

export const flattenInvoice = (invoiceLike) => {
  const source = toObject(invoiceLike);
  const normalized = { ...source };

  if (Array.isArray(normalized.items)) {
    normalized.items = normalized.items.map(normalizeLine);
  }
  if (Array.isArray(normalized.lines)) {
    normalized.lines = normalized.lines.map(normalizeLine);
  }

  return normalized;
};

const sanitizeForm = (formKey, value) => {
  const objectValue = toObject(value);
  if (formKey === INVOICE_FORM_KEY) {
    return flattenInvoice(objectValue);
  }
  return objectValue;
};

const buildInvoiceReset = (current) => {
  const currentObj = toObject(current);
  const preserved = {};
  INVOICE_PRESERVE_KEYS.forEach((key) => {
    if (currentObj[key] != null) {
      preserved[key] = currentObj[key];
    }
  });
  return {
    ...preserved,
    items: [],
    payments: [],
    notes: "",
  };
};

const uiStateSlice = createSlice({
  name: "uiState",
  initialState,
  reducers: {
    updateFormData(state, action) {
      const payload = action.payload || {};
      const formKey = getFormKey(payload);
      if (!formKey) return;

      const patch = toObject(payload.data ?? payload.value ?? payload.formData);
      const previous = toObject(state.forms[formKey]);
      state.forms[formKey] = sanitizeForm(formKey, { ...previous, ...patch });
    },

    setForm(state, action) {
      const payload = action.payload || {};
      const formKey = getFormKey(payload);
      if (!formKey) return;

      state.forms[formKey] = sanitizeForm(
        formKey,
        payload.data ?? payload.value ?? payload.formData
      );
    },

    replaceForm(state, action) {
      const payload = action.payload || {};
      const formKey = getFormKey(payload);
      if (!formKey) return;

      state.forms[formKey] = sanitizeForm(
        formKey,
        payload.data ?? payload.value ?? payload.formData
      );
    },

    resetFormData(state, action) {
      const payload = action.payload;
      const formKey = typeof payload === "string" ? payload : getFormKey(payload || {});

      if (!formKey) {
        state.forms = {};
        return;
      }

      if (formKey === INVOICE_FORM_KEY) {
        state.forms[formKey] = buildInvoiceReset(state.forms[formKey]);
        return;
      }

      delete state.forms[formKey];
    },

    setFlowField(state, action) {
      const payload = action.payload || {};
      const flowKey = payload.flow ?? payload.flowName ?? payload.name ?? payload.key ?? null;
      const field = payload.field ?? payload.path ?? null;
      if (!flowKey || !field) return;

      if (!state.flows[flowKey]) {
        state.flows[flowKey] = {};
      }
      state.flows[flowKey][field] = payload.value;
    },

    mergeFlow(state, action) {
      const payload = action.payload || {};
      const flowKey = payload.flow ?? payload.flowName ?? payload.name ?? payload.key ?? null;
      if (!flowKey) return;

      const patch = toObject(payload.data ?? payload.value ?? payload.flowData);
      const previous = toObject(state.flows[flowKey]);
      state.flows[flowKey] = { ...previous, ...patch };
    },

    clearFlow(state, action) {
      const payload = action.payload;
      const flowKey = typeof payload === "string" ? payload : payload?.flow ?? payload?.flowName;
      if (!flowKey) return;
      delete state.flows[flowKey];
    },

    resetFlow(state, action) {
      const payload = action.payload;
      const flowKey = typeof payload === "string" ? payload : payload?.flow ?? payload?.flowName;

      if (!flowKey) {
        state.flows = {};
        return;
      }

      state.flows[flowKey] = {};
    },
  },
});

export const {
  updateFormData,
  setForm,
  replaceForm,
  resetFormData,
  setFlowField,
  mergeFlow,
  clearFlow,
  resetFlow,
} = uiStateSlice.actions;

export default uiStateSlice.reducer;
